import './style.css';
import { AU, SOLAR_MASS, YEAR, DAY } from './engines/constants';
import { orbitalPeriod, visViva, circularOrbitalVelocity, orbitalStateAtTime } from './engines/orbital';
import {
  luminosityFromMass,
  radiusFromMass,
  effectiveTemperatureFromSolarUnits,
  mainSequenceLifetimeYears,
  spectralTypeFromTemperature,
  hrDiagramPosition,
} from './engines/stellar';

// ============================================================
// 言語切り替え
// ============================================================
const langBtn = document.getElementById('langToggle') as HTMLButtonElement;
let currentLang: 'ja' | 'en' = 'ja';

function syncSliderFill(input: HTMLInputElement) {
  const min = parseFloat(input.min || '0');
  const max = parseFloat(input.max || '100');
  const val = parseFloat(input.value);
  const percent = ((val - min) / (max - min)) * 100;
  input.style.setProperty('--range-progress', `${percent}%`);
}

function applyLang(lang: 'ja' | 'en') {
  currentLang = lang;
  document.documentElement.lang = lang;
  document.querySelectorAll<HTMLElement>('[data-ja]').forEach((el) => {
    el.textContent = lang === 'ja' ? el.getAttribute('data-ja') : el.getAttribute('data-en');
  });
  langBtn.querySelectorAll<HTMLElement>('.opt').forEach((opt) => {
    opt.classList.toggle('on', opt.getAttribute('data-lang-opt') === lang);
  });
  const footerMessageJa = document.getElementById('footerMessageJa');
  const footerMessageEn = document.getElementById('footerMessageEn');
  if (footerMessageJa && footerMessageEn) {
    (footerMessageJa as HTMLElement).style.display = lang === 'ja' ? 'block' : 'none';
    (footerMessageEn as HTMLElement).style.display = lang === 'en' ? 'block' : 'none';
  }
}
langBtn.addEventListener('click', () => applyLang(currentLang === 'ja' ? 'en' : 'ja'));

// ============================================================
// オンボーディング（初回のみ表示、localStorageで記憶）
// ============================================================
const ONBOARDING_KEY = 'cosmos-simulator:onboarding-seen';
const onboarding = document.getElementById('onboarding') as HTMLElement;
const onboardingDismiss = document.getElementById('onboardingDismiss') as HTMLButtonElement;
const helpBtn = document.getElementById('helpBtn') as HTMLButtonElement;

function showOnboarding() {
  onboarding.classList.remove('hidden');
}
function hideOnboarding() {
  onboarding.classList.add('hidden');
  try {
    localStorage.setItem(ONBOARDING_KEY, '1');
  } catch {
    /* localStorageが使えない環境でも動作は継続する */
  }
}
onboardingDismiss.addEventListener('click', hideOnboarding);
helpBtn.addEventListener('click', showOnboarding);

let hasSeenOnboarding = false;
try {
  hasSeenOnboarding = localStorage.getItem(ONBOARDING_KEY) === '1';
} catch {
  hasSeenOnboarding = false;
}
if (hasSeenOnboarding) onboarding.classList.add('hidden');

// ============================================================
// DOM参照（惑星以外）
// ============================================================
const massSlider = document.getElementById('massSlider') as HTMLInputElement;
const massVal = document.getElementById('massVal') as HTMLElement;
const tempNum = document.getElementById('tempNum') as HTMLElement;
const lumNum = document.getElementById('lumNum') as HTMLElement;
const radiusVal = document.getElementById('radiusVal') as HTMLElement;
const lifetimeVal = document.getElementById('lifetimeVal') as HTMLElement;
const hzInnerValEl = document.getElementById('hzInnerVal') as HTMLElement;
const hzOuterValEl = document.getElementById('hzOuterVal') as HTMLElement;
const periodBVal = document.getElementById('periodBVal') as HTMLElement;
const speedBVal = document.getElementById('speedBVal') as HTMLElement;
const refLine = document.getElementById('refLine') as HTMLElement;
const validationNote = document.getElementById('validationNote') as HTMLElement;
const scaleLabel = document.getElementById('scaleLabel') as HTMLElement;

const hzRingInner = document.getElementById('hzRingInner') as HTMLElement;
const hzRingOuter = document.getElementById('hzRingOuter') as HTMLElement;
const canvasWrap = document.getElementById('canvasWrap') as HTMLElement;
const starCore = document.getElementById('starCore') as HTMLElement;
const trailLayer = document.getElementById('trailLayer') as HTMLElement;
const hrStarSun = document.getElementById('hrStarSun') as HTMLElement;
const hrStarSel = document.getElementById('hrStarSel') as HTMLElement;
const formulaBox = document.getElementById('formulaBox') as HTMLElement;
const planetListEl = document.getElementById('planetList') as HTMLElement;
const addPlanetBtn = document.getElementById('addPlanetBtn') as HTMLButtonElement;

let massSolar = parseFloat(massSlider.value);
let pxPerAU = 20;

// 太陽の位置はM☉=1.00で固定なので一度だけ計算する
const sunPos = hrDiagramPosition(5778, 1.0);
hrStarSun.style.left = `${(sunPos.x * 100).toFixed(2)}%`;
hrStarSun.style.top = `${(sunPos.y * 100).toFixed(2)}%`;

// ============================================================
// 惑星（動的に追加・削除できる配列として管理）
// ============================================================
const TRAIL_LENGTH = 14;

interface Planet {
  id: string;
  aAU: number;
  e: number;
  nameJa: string;
  nameEn: string;
  // DOM
  dotEl: HTMLElement;
  ringEl: HTMLElement;
  itemEl: HTMLElement;
  dotItemEl: HTMLElement;
  tagEl: HTMLElement;
  nameEl: HTMLElement;
  removeBtn: HTMLButtonElement;
  // トレイル
  trailHistory: { px: number; py: number }[];
  trailPool: HTMLElement[];
}

const planets: Planet[] = [];
let planetCounter = 0;
const PLANET_LETTERS = 'bcdefghijklmnopqrstuvwxyz';

function createPlanet(aAU: number, e: number, nameJa: string, nameEn: string): Planet {
  const id = `planet-${planetCounter++}`;

  // 軌道リング + 惑星ドット（キャンバス側）
  const ringEl = document.createElement('div');
  ringEl.className = 'orbit-ring';
  canvasWrap.insertBefore(ringEl, trailLayer);

  const dotEl = document.createElement('div');
  dotEl.className = 'planet-dot';
  canvasWrap.appendChild(dotEl);

  // 惑星リストアイテム（左パネル側）
  const itemEl = document.createElement('div');
  itemEl.className = 'planet-item';

  const dotItemEl = document.createElement('div');
  dotItemEl.className = 'dot';

  const nameEl = document.createElement('div');
  nameEl.className = 'name';
  nameEl.setAttribute('data-ja', nameJa);
  nameEl.setAttribute('data-en', nameEn);
  nameEl.textContent = currentLang === 'ja' ? nameJa : nameEn;

  const tagEl = document.createElement('div');
  tagEl.className = 'tag';

  const removeBtn = document.createElement('button');
  removeBtn.className = 'remove-btn';
  removeBtn.textContent = '×';
  removeBtn.title = currentLang === 'ja' ? 'この惑星を削除' : 'Remove this planet';

  itemEl.appendChild(dotItemEl);
  itemEl.appendChild(nameEl);
  itemEl.appendChild(tagEl);
  itemEl.appendChild(removeBtn);
  planetListEl.appendChild(itemEl);

  // トレイル用のプール
  const trailPool: HTMLElement[] = [];
  for (let i = 0; i < TRAIL_LENGTH; i++) {
    const trailDot = document.createElement('div');
    trailDot.className = 'trail-dot';
    trailDot.style.opacity = '0';
    trailLayer.appendChild(trailDot);
    trailPool.push(trailDot);
  }

  const planet: Planet = {
    id, aAU, e, nameJa, nameEn,
    dotEl, ringEl, itemEl, dotItemEl, tagEl, nameEl, removeBtn,
    trailHistory: [],
    trailPool,
  };

  removeBtn.addEventListener('click', () => removePlanet(planet));

  planets.push(planet);
  return planet;
}

function removePlanet(planet: Planet) {
  planet.ringEl.remove();
  planet.dotEl.remove();
  planet.itemEl.remove();
  planet.trailPool.forEach((d) => d.remove());
  const idx = planets.indexOf(planet);
  if (idx >= 0) planets.splice(idx, 1);
  updateScaleAndRings();
  updateStarReadouts();
}

function addRandomPlanet() {
  // 対数一様分布でAUをランダム生成（0.3〜7AUの範囲に自然に分布させる）
  const logMin = Math.log10(0.3);
  const logMax = Math.log10(7);
  const aAU = Math.pow(10, logMin + Math.random() * (logMax - logMin));
  const e = Math.random() * 0.15;
  const letter = PLANET_LETTERS[planets.length] ?? String(planets.length);
  const nameJa = `Planet ${letter}`;
  const nameEn = `Planet ${letter}`;
  createPlanet(aAU, e, nameJa, nameEn);
  updateScaleAndRings();
  updateStarReadouts();
}

addPlanetBtn.addEventListener('click', addRandomPlanet);

// 初期の2惑星（地球型 + ガス巨星）
const planetB = createPlanet(1.2, 0.02, 'Planet b (地球型)', 'Planet b (Earth-like)');
const planetC = createPlanet(4.5, 0.05, 'Planet c (ガス巨星)', 'Planet c (Gas Giant)');

// ============================================================
// 恒星読み取り値・HZ判定・HR図位置の更新
// ============================================================
function toSuperscript(n: number): string {
  const map: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' };
  return String(n).split('').map((c) => map[c] ?? c).join('');
}

function setHZTag(planet: Planet, inHZ: boolean) {
  planet.itemEl.classList.toggle('hz', inHZ);
  planet.dotItemEl.style.background = inHZ ? 'var(--teal)' : 'var(--text-faint)';
  planet.dotItemEl.style.boxShadow = inHZ ? '0 0 6px rgba(79,209,197,0.6)' : 'none';
  planet.tagEl.setAttribute('data-ja', inHZ ? 'HZ内' : 'HZ外');
  planet.tagEl.setAttribute('data-en', inHZ ? 'In HZ' : 'Outside HZ');
  planet.tagEl.textContent = currentLang === 'ja' ? planet.tagEl.getAttribute('data-ja') : planet.tagEl.getAttribute('data-en');
}

function hzInnerOuterAU(luminositySolar: number): [number, number] {
  return [0.95 * Math.sqrt(luminositySolar), 1.67 * Math.sqrt(luminositySolar)];
}

function updateStarReadouts() {
  const L = luminosityFromMass(massSolar);
  const R = radiusFromMass(massSolar);
  const T = effectiveTemperatureFromSolarUnits(L, R);
  const lifetime = mainSequenceLifetimeYears(massSolar, L);
  const [hzIn, hzOut] = hzInnerOuterAU(L);

  massVal.textContent = massSolar.toFixed(2);
  tempNum.textContent = Math.round(T).toLocaleString();
  lumNum.textContent = L < 100 ? L.toFixed(2) : L.toExponential(2);
  radiusVal.textContent = `${R.toFixed(2)} R☉`;

  const exp = Math.floor(Math.log10(lifetime));
  const mantissa = lifetime / Math.pow(10, exp);
  const yrWord = currentLang === 'ja' ? '年' : 'yr';
  lifetimeVal.innerHTML = `${mantissa.toFixed(2)} × 10${toSuperscript(exp)} <span data-ja="年" data-en="yr">${yrWord}</span>`;

  hzInnerValEl.textContent = `${hzIn.toFixed(2)} AU`;
  hzOuterValEl.textContent = `${hzOut.toFixed(2)} AU`;

  planets.forEach((p) => {
    const inHZ = p.aAU >= hzIn && p.aAU <= hzOut;
    setHZTag(p, inHZ);
  });

  // 参照値: 1AU上の円軌道 (質量=1.00のとき実測の地球値と一致するはず)
  const MKg = massSolar * SOLAR_MASS;
  const refPeriodDays = orbitalPeriod(1 * AU, MKg) / DAY;
  const refVelocityKms = circularOrbitalVelocity(1 * AU, MKg) / 1000;
  const dayWord = currentLang === 'ja' ? '日' : 'days';
  refLine.textContent = `${refPeriodDays.toFixed(2)} ${dayWord} / ${refVelocityKms.toFixed(2)} km/s`;

  if (Math.abs(massSolar - 1.0) < 0.005) {
    const periodErr = Math.abs((refPeriodDays - 365.25) / 365.25) * 100;
    const velErr = Math.abs((refVelocityKms - 29.78) / 29.78) * 100;
    const maxErr = Math.max(periodErr, velErr);
    validationNote.setAttribute('data-ja', `Validation: 地球実測値との誤差 ${maxErr.toFixed(3)}%`);
    validationNote.setAttribute('data-en', `Validation: ${maxErr.toFixed(3)}% error vs measured Earth`);
  } else {
    validationNote.setAttribute('data-ja', 'Validation: M☉=1.00にすると地球実測値と照合できます');
    validationNote.setAttribute('data-en', 'Validation: set M☉=1.00 to compare against measured Earth');
  }
  validationNote.textContent = currentLang === 'ja' ? validationNote.getAttribute('data-ja') : validationNote.getAttribute('data-en');

  const type = spectralTypeFromTemperature(T);
  document.querySelectorAll<HTMLElement>('#spectralRow span').forEach((el) => {
    el.classList.toggle('sel', el.getAttribute('data-type') === type);
  });

  const coreSize = Math.max(18, Math.min(70, 34 * Math.pow(massSolar, 0.35)));
  starCore.style.width = `${coreSize}px`;
  starCore.style.height = `${coreSize}px`;

  const pos = hrDiagramPosition(T, L);
  hrStarSel.style.left = `${(pos.x * 100).toFixed(2)}%`;
  hrStarSel.style.top = `${(pos.y * 100).toFixed(2)}%`;
}

function setRing(el: HTMLElement, diameterPx: number) {
  el.style.width = `${diameterPx}px`;
  el.style.height = `${diameterPx}px`;
}

function updateScaleAndRings() {
  const L = luminosityFromMass(massSolar);
  const [hzIn, hzOut] = hzInnerOuterAU(L);
  const farthestPlanetAU = planets.length > 0
    ? Math.max(...planets.map((p) => p.aAU * (1 + p.e)))
    : 1;
  const maxAU = Math.max(farthestPlanetAU, hzOut) * 1.12;

  const rect = canvasWrap.getBoundingClientRect();
  const maxPxRadius = Math.min(rect.width, rect.height) / 2 - 24;
  pxPerAU = maxPxRadius / maxAU;

  scaleLabel.textContent = `1px = ${(1 / pxPerAU).toFixed(3)} AU`;

  setRing(hzRingInner, hzIn * pxPerAU * 2);
  setRing(hzRingOuter, hzOut * pxPerAU * 2);
  planets.forEach((p) => setRing(p.ringEl, p.aAU * pxPerAU * 2));
}

function positionPlanet(dotEl: HTMLElement, xM: number, yM: number) {
  const px = (xM / AU) * pxPerAU;
  const py = (yM / AU) * pxPerAU;
  dotEl.style.left = `calc(50% + ${px}px)`;
  dotEl.style.top = `calc(50% + ${py}px)`;
  return { px, py };
}

function updateTrail(planet: Planet, px: number, py: number) {
  planet.trailHistory.unshift({ px, py });
  if (planet.trailHistory.length > TRAIL_LENGTH) planet.trailHistory.pop();
  planet.trailHistory.forEach((pos, i) => {
    const dot = planet.trailPool[i];
    if (!dot) return;
    dot.style.left = `calc(50% + ${pos.px}px)`;
    dot.style.top = `calc(50% + ${pos.py}px)`;
    dot.style.opacity = `${(1 - i / TRAIL_LENGTH) * 0.35}`;
  });
}

// ============================================================
// 軌道アニメーション
// ============================================================
const SIM_YEARS_PER_SECOND = 0.4;
let simTimeS = 0;
let lastFrameMs: number | null = null;

function tick(nowMs: number) {
  if (lastFrameMs === null) lastFrameMs = nowMs;
  const dtMs = nowMs - lastFrameMs;
  lastFrameMs = nowMs;
  simTimeS += (dtMs / 1000) * SIM_YEARS_PER_SECOND * YEAR;

  const MKg = massSolar * SOLAR_MASS;

  planets.forEach((p) => {
    const state = orbitalStateAtTime(simTimeS, { semiMajorAxisM: p.aAU * AU, eccentricity: p.e, totalMassKg: MKg });
    const pos = positionPlanet(p.dotEl, state.x, state.y);
    updateTrail(p, pos.px, pos.py);

    if (p === planetB) {
      const speedB = visViva(state.r, p.aAU * AU, MKg);
      const period = orbitalPeriod(p.aAU * AU, MKg);
      const dayWord = currentLang === 'ja' ? '日' : 'days';
      periodBVal.innerHTML = `${(period / DAY).toFixed(2)} <span data-ja="日" data-en="days">${dayWord}</span>`;
      speedBVal.textContent = `${(speedB / 1000).toFixed(2)} km/s`;
    }
  });

  requestAnimationFrame(tick);
}

// ============================================================
// プリセット（実在の恒星: 太陽・シリウスA・プロキシマ・ケンタウリ）
// ============================================================
const PRESETS: Record<string, number> = {
  sun: 1.0,
  sirius: 2.063,
  proxima: 0.122,
};

document.querySelectorAll<HTMLButtonElement>('.preset-btn').forEach((btn) => {
  btn.addEventListener('click', () => {
    const key = btn.getAttribute('data-preset');
    if (!key || !(key in PRESETS)) return;
    massSlider.value = String(PRESETS[key]);
    document.querySelectorAll('.preset-btn').forEach((b) => b.classList.remove('active'));
    btn.classList.add('active');
    onMassChange();
  });
});

// ============================================================
// 初期化
// ============================================================
function onMassChange() {
  massSolar = parseFloat(massSlider.value);
  syncSliderFill(massSlider);
  updateStarReadouts();
  updateScaleAndRings();
}

massSlider.addEventListener('input', () => {
  document.querySelectorAll('.preset-btn').forEach((b) => b.classList.remove('active'));
  onMassChange();
});
window.addEventListener('resize', updateScaleAndRings);

onMassChange();
requestAnimationFrame(tick);

// ページローダーを隠す（初期計算・初回描画が終わったタイミング）
const pageLoader = document.getElementById('pageLoader');
if (pageLoader) {
  requestAnimationFrame(() => {
    pageLoader.classList.add('hide');
    setTimeout(() => pageLoader.remove(), 400);
  });
}

// ============================================================
// フッターメッセージ（クリックで開閉）
// ============================================================
const dedicationFooter = document.getElementById('dedicationFooter');
const footerMessageOverlay = document.getElementById('footerMessageOverlay');
const footerMessageClose = document.getElementById('footerMessageClose');
if (dedicationFooter && footerMessageOverlay && footerMessageClose) {
  dedicationFooter.addEventListener('click', () => {
    footerMessageOverlay.classList.add('show');
  });
  footerMessageClose.addEventListener('click', () => {
    footerMessageOverlay.classList.remove('show');
  });
  footerMessageOverlay.addEventListener('click', (e) => {
    if (e.target === footerMessageOverlay) footerMessageOverlay.classList.remove('show');
  });
}
